import { getDatabase } from '@fabric-app/compat';
import { v4 } from 'uuid';
import { createLogger } from '@fabric-app/utils/logging';
import { isTruthy } from '@fabric-app/utils/guards';
import { getWoodyClient } from '../../woody/woody.js';
import { isWoodyError } from '../../errors/errors.utils.js';
import * as Y from 'yjs';
import { simpleHashUint8Array } from '@fabric-app/utils/text';
import pLimit from 'p-limit';

const STORE_NAME = "notepad-updates";
const BG_SYNC_INTERVAL = 1e3 * 60 * 2;
const PENDING_ATTEMPT_INTERVAL = 1e3 * 60 * 1;
const SENT_ATTEMPT_INTERVAL = 1e3 * 60 * 60 * 2;
const SENT_GARBAGE_COLLECTION_INTERVAL = 1e3 * 60 * 60 * 6;
const isNotepadUpdate = (update) => {
  return typeof update === "object" && update !== null && "id" in update && typeof update.id === "string" && "resourceId" in update && typeof update.resourceId === "string" && "instanceId" in update && typeof update.instanceId === "string" && "update" in update && update.update instanceof Uint8Array && "hash" in update && typeof update.hash === "string" && "status" in update && (update.status === "pending" || update.status === "sent") && "sentAt" in update && (update.sentAt === null || typeof update.sentAt === "number") && "createdAt" in update && typeof update.createdAt === "number" && "lastAttempt" in update && (update.lastAttempt === null || typeof update.lastAttempt === "number");
};
class NotepadUpdateFailsafeService {
  constructor() {
    this.syncInterval = null;
    this.logger = createLogger("NotepadUpdateFailsafeService");
    this.instanceId = v4();
  }
  get storage() {
    return getDatabase();
  }
  static getInstance() {
    if (!NotepadUpdateFailsafeService.instance) {
      NotepadUpdateFailsafeService.instance = new NotepadUpdateFailsafeService();
    }
    return NotepadUpdateFailsafeService.instance;
  }
  async storeUpdate(resourceId, update) {
    if (!this.storage) {
      this.logger.error("No storage available, retrying in 1 second");
      return new Promise((resolve) => {
        setTimeout(() => {
          this.storeUpdate(resourceId, update).then(resolve);
        }, 1e3);
      });
    }
    const id = `${resourceId}-${v4()}`;
    const notepadUpdate = {
      id,
      resourceId,
      instanceId: this.instanceId,
      update,
      hash: simpleHashUint8Array(update).toString(36),
      status: "pending",
      sentAt: null,
      createdAt: Date.now(),
      lastAttempt: null
    };
    this.logger.logDevOnly(`Storing update for resource ${resourceId}:`, notepadUpdate);
    await this.storage.setItem(STORE_NAME, id, notepadUpdate);
    return id;
  }
  async sendUpdates(resourceId, delta, updateIds, lastCreatedAt) {
    const client = await getWoodyClient();
    this.logger.logDevOnly(`Sending updates for resource ${resourceId}:`, {
      delta,
      updateIds
    });
    try {
      await client.v2({
        endpoint: "/v2/notepads/{resourceId}/state",
        params: {
          resourceId
        }
      }, {
        method: "patch",
        body: new Blob([
          delta
        ], {
          type: "application/octet-stream"
        }),
        query: {
          modifiedAt: new Date(lastCreatedAt).toISOString()
        }
      });
      await this.markAsSent(updateIds);
    } catch (error) {
      if (isWoodyError(error) && (error.status === 404 || error.status === 403)) {
        for (const id of updateIds) {
          await this.storage?.removeItem(STORE_NAME, id);
        }
      } else {
        this.logger.error(`Failed to send update ${updateIds.join(", ")} for resource ${resourceId}:`, error);
      }
      await this.markAsFailed(updateIds);
    }
  }
  async markAsSent(ids) {
    if (!this.storage) {
      this.logger.error("No storage available");
      return;
    }
    this.logger.logDevOnly(`Marking updates as sent:`, ids);
    for (const id of ids) {
      const update = await this.storage.getItem(STORE_NAME, id);
      if (update && isNotepadUpdate(update)) {
        update.status = "sent";
        update.sentAt = update.sentAt ?? Date.now();
        update.lastAttempt = Date.now();
        update.instanceId = this.instanceId;
        await this.storage.setItem(STORE_NAME, id, update);
      }
    }
  }
  async markAsSentByHash(resourceId, hashes) {
    if (!this.storage) {
      this.logger.error("No storage available");
      return;
    }
    this.logger.logDevOnly(`Marking resource ${resourceId} updates as sent by hash:`, hashes);
    const updates = await this.getUpdates(resourceId);
    const ids = [];
    for (const update of updates) {
      if (hashes.includes(update.hash)) {
        ids.push(update.id);
      }
    }
    if (ids.length > 0) {
      await this.markAsSent(ids);
    }
  }
  async markAsFailed(ids) {
    if (!this.storage) {
      this.logger.error("No storage available");
      return;
    }
    this.logger.logDevOnly(`Marking updates as failed:`, ids);
    for (const id of ids) {
      const update = await this.storage.getItem(STORE_NAME, id);
      if (update && isNotepadUpdate(update) && update.status === "pending") {
        update.lastAttempt = Date.now();
        update.instanceId = this.instanceId;
        await this.storage.setItem(STORE_NAME, id, update);
      }
    }
  }
  async getUpdates(resourceId) {
    const storage = this.storage;
    if (!storage) {
      this.logger.error("No storage available");
      return [];
    }
    const allKeys = (await storage.keys(STORE_NAME)).filter((key) => resourceId ? key.startsWith(resourceId) : true);
    const allUpdates = await Promise.all(allKeys.map(async (key) => await storage.getItem(STORE_NAME, key)));
    return allUpdates.filter((update) => !resourceId || update?.resourceId === resourceId).filter(isTruthy).filter(isNotepadUpdate).sort((a, b) => a.createdAt - b.createdAt);
  }
  async processUpdates() {
    if (!this.storage) {
      this.logger.error("No storage available");
      return;
    }
    const pendingUpdates = await this.getUpdates();
    this.logger.logDevOnly(`Found ${pendingUpdates.length} updates`);
    const resourceMap = /* @__PURE__ */ new Map();
    let totalPendingUpdates = 0;
    let totalSentUpdates = 0;
    let totalCleanedUpdates = 0;
    for (const update of pendingUpdates) {
      if (update.status === "sent" && update.sentAt && update.sentAt + SENT_GARBAGE_COLLECTION_INTERVAL < Date.now()) {
        await this.storage.removeItem(STORE_NAME, update.id);
        totalCleanedUpdates++;
        continue;
      }
      if (!resourceMap.has(update.resourceId)) {
        resourceMap.set(update.resourceId, {
          updates: [],
          hasPendingUpdate: false,
          sentAt: null,
          lastAttempt: null,
          instanceId: update.instanceId,
          lastCreatedAt: update.createdAt
        });
      }
      if (update.status === "pending") {
        totalPendingUpdates++;
      } else if (update.status === "sent") {
        totalSentUpdates++;
      }
      const resource = resourceMap.get(update.resourceId);
      resourceMap.set(update.resourceId, {
        ...resource,
        updates: [
          ...resource.updates,
          update
        ],
        hasPendingUpdate: resource.hasPendingUpdate || update.status === "pending",
        sentAt: Math.max(resource.sentAt ?? 0, update.sentAt ?? 0),
        lastAttempt: Math.max(resource.lastAttempt ?? 0, update.lastAttempt ?? 0),
        instanceId: update.instanceId,
        lastCreatedAt: Math.max(resource.lastCreatedAt ?? 0, update.createdAt)
      });
    }
    this.logger.logDevOnly(`Running processUpdates:`, {
      totalPendingUpdates,
      totalSentUpdates,
      totalCleanedUpdates
    });
    const limit = pLimit(3);
    const updates = [];
    for (const [resourceId, resource] of resourceMap.entries()) {
      if (resource.hasPendingUpdate && (this.instanceId !== resource.instanceId || !resource.lastAttempt || resource.lastAttempt + PENDING_ATTEMPT_INTERVAL < Date.now())) {
        updates.push(() => this.sendUpdates(resourceId, Y.mergeUpdates(resource.updates.map((u) => u.update)), resource.updates.map((u) => u.id), resource.lastCreatedAt));
      }
      if (!resource.hasPendingUpdate && (!resource.lastAttempt || resource.lastAttempt + SENT_ATTEMPT_INTERVAL < Date.now())) {
        updates.push(() => this.sendUpdates(resourceId, Y.mergeUpdates(resource.updates.map((u) => u.update)), resource.updates.map((u) => u.id), resource.lastCreatedAt));
      }
    }
    await Promise.allSettled(updates.map((u) => limit(u)));
  }
  startBackgroundSync() {
    if (this.syncInterval) return;
    this.processUpdates().catch((error) => this.logger.error("Failed to process pending updates:", error));
    this.syncInterval = window.setInterval(() => {
      this.processUpdates().catch((error) => this.logger.error("Failed to process pending updates:", error));
    }, BG_SYNC_INTERVAL);
  }
  stopBackgroundSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }
}
const notepadUpdateService = NotepadUpdateFailsafeService.getInstance();

export { NotepadUpdateFailsafeService, notepadUpdateService };
//# sourceMappingURL=NotepadUpdateFailsafeService.js.map
